import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './FeedbackForm.scss';
import { getFeedback, postFeedback } from '@Src/features/Dashboard/Service/Dashboard.service';
import toast from 'react-hot-toast';
import { APIStatus } from '@Src/services/baseInterfaces';
import { useAppDispatch } from '@Src/app/hooks';
import { showSpinner } from '@Src/app/reducers/globalSlice';

interface FeedbackFormProps {
    onSubmit?: (values: FeedbackValues) => void;
    initialValues?: FeedbackValues;
    isModal?: boolean;
    UserTestId: string;
}

export interface FeedbackValues {
    rate: number;
    comment: string;
}

const StarRating = ({ value, onChange, isModal, readonly }: { value: number; onChange?: (val: number) => void; isModal?: boolean; readonly?: boolean }) => {
    const stars = [1, 2, 3, 4, 5];

    const handleClick = (e: React.MouseEvent<HTMLDivElement>, star: number) => {
        if (readonly || !onChange) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        if (x < width / 2) {
            onChange(star - 0.5);
        } else {
            onChange(star);
        }
    };

    return (
        <div className={`star-rating ${readonly ? 'readonly' : ''}`}>
            {stars.map((star) => {
                const isFull = star <= value;
                const isHalf = !isFull && (star - 0.5 <= value);
                
                return (
                    <div
                        key={star}
                        className={`star ${isFull ? 'full' : isHalf ? 'half' : ''}`}
                        onClick={(e) => handleClick(e, star)}
                        style={{ 
                            fontSize: isModal ? "25px" : "32px", 
                            cursor: readonly ? 'default' : 'pointer',
                            position: 'relative',
                            display: 'inline-flex'
                        }}
                    >
                        <i className="pi pi-star" style={{ fontSize: isModal ? "25px" : "32px", color: (isFull || isHalf) ? 'transparent' : '#d0d5dd' }}></i>
                        {(isFull || isHalf) && (
                            <i 
                                className="pi pi-star-fill" 
                                style={{ 
                                    fontSize: isModal ? "25px" : "32px", 
                                    position: 'absolute', 
                                    left: 0, 
                                    top: 0,
                                    color: '#F2BB13',
                                    clipPath: isHalf ? 'inset(0 50% 0 0)' : 'none',
                                    WebkitClipPath: isHalf ? 'inset(0 50% 0 0)' : 'none'
                                }}
                            ></i>
                        )}
                        {isHalf && (
                            <i 
                                className="pi pi-star" 
                                style={{ 
                                    fontSize: isModal ? "25px" : "32px", 
                                    position: 'absolute', 
                                    left: 0, 
                                    top: 0,
                                    color: '#d0d5dd',
                                    clipPath: 'inset(0 0 0 50%)',
                                    WebkitClipPath: 'inset(0 0 0 50%)'
                                }}
                            ></i>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSubmit, initialValues, isModal, UserTestId }) => {
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [feedbackDetails, setFeedbackDetails] = useState<any>({});
    const dispatch = useAppDispatch();
    const formik = useFormik<FeedbackValues>({
        initialValues: initialValues || {
            rate: 0,
            comment: '',
        },
        validationSchema: Yup.object({
            rate: Yup.number().min(0.5, 'Please provide a rating').required('Please provide a rating'),
            comment: Yup.string().max(200, 'Comment must be less than 200 characters').required('Add some comment first'),
        }),
        onSubmit: async (values) => {
            if (onSubmit) {
                onSubmit(values);
            } else {
                const payload = {
                    Rate: values.rate,
                    comment: values.comment,
                    UserTestId: UserTestId
                }
                try {
                    const response = await postFeedback(payload);
                    if (response.ResponseType === "SUCCESS") {
                        setIsSubmitted(true);
                    } else {
                        toast.error("Something went wrong")
                    }

                } catch (error) {
                    console.log(error);
                    toast.error("Something went wrong")

                }
            }

        },
    });
    const getFeedbackDetails = async () => {
        // dispatch(showSpinner(true));
        try {
            const response = await getFeedback({ UserTestId });
            if (response.ResponseType === APIStatus.SUCCESS && response.data.length > 0) {
                setFeedbackDetails(response.data[0]);
            }
            // dispatch(showSpinner(false));
        } catch (error) {
            toast.error("Something went wrong while fetching feedback data");
            // dispatch(showSpinner(false));
        }
    }

    useEffect(() => {
        getFeedbackDetails();
    }, [UserTestId])
    if (isSubmitted) {
        return (
            <div className={`feedback-form-container ${isModal ? 'modal-view' : ''}`}>
                <div className="success-message">
                    <i className="pi pi-check-circle" style={{ fontSize: '3rem', color: '#58b37b', marginBottom: '16px' }}></i>
                    <h3>Thank You!</h3>
                    <p>Your feedback has been submitted successfully.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {
                !(feedbackDetails?.IsFeedback === 1) ? <div className={`feedback-form-container ${isModal ? 'modal-view' : ''}`}>
                    {!isModal && <h3>How was this test?</h3>}
                    <form onSubmit={formik.handleSubmit}>
                        <div className="form-field">
                            <label>Rate your experience <span className='tw-text-red-500'>*</span></label>
                            <StarRating
                                value={formik.values.rate}
                                onChange={(val) => formik.setFieldValue('rate', val)}
                                isModal={isModal}
                            />
                            {formik.touched.rate && formik.errors.rate ? (
                                <div className="error-text text-center">{formik.errors.rate}</div>
                            ) : null}
                        </div>

                        <div className="form-field">
                            <label>Comment <span className='tw-text-red-500'>*</span></label>
                            <div className="textarea-wrapper">
                                <textarea
                                    name="comment"
                                    rows={4}
                                    maxLength={200}
                                    placeholder="Tell us what you think..."
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.comment}
                                />
                                <span className="char-count">
                                    {formik.values.comment.length}/200
                                </span>
                            </div>
                            {formik.touched.comment && formik.errors.comment ? (
                                <div className="error-text">{formik.errors.comment}</div>
                            ) : null}
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-btn" disabled={formik.isSubmitting}>
                                Submit Feedback
                            </button>
                        </div>
                    </form>
                </div> : 
                isModal ? (
                    <div className="feedback-form-container modal-view">
                        <div className="form-field">
                            <label>Rating</label>
                            <StarRating
                                value={feedbackDetails.Rate}
                                isModal={true}
                                readonly={true}
                            />
                        </div>
                        <div className="form-field">
                            <label>Comment</label>
                            <div className="view-comment">
                                {feedbackDetails.comment}
                            </div>
                        </div>
                    </div>
                ) : null
            }
        </>

    );
};
